'use client';

import { useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import { Box, token } from '@terra/ui-styled-system';
import type { ParsedAdGraphOutput } from './parse-ad-graph-output.util';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 40;
const MAX_CHAT_GRAPH_NODES = 80;

const NODE_COLORS = {
  crownJewelBorder: token('colors.red.9'),
  crownJewelBackground: token('colors.red.3'),
  ownedBorder: token('colors.terra.8'),
  ownedBackground: token('colors.terra.3'),
  defaultBorder: token('colors.border.default'),
  defaultBackground: token('colors.bg.subtle'),
  abusableEdge: token('colors.red.9'),
  defaultEdge: token('colors.gray.8'),
} as const;

function resolveNodeColors(node: {
  isCrownJewel?: boolean;
  isOwned?: boolean;
}): {
  border: string;
  background: string;
} {
  if (node.isCrownJewel) {
    return {
      border: NODE_COLORS.crownJewelBorder,
      background: NODE_COLORS.crownJewelBackground,
    };
  }
  if (node.isOwned) {
    return {
      border: NODE_COLORS.ownedBorder,
      background: NODE_COLORS.ownedBackground,
    };
  }
  return {
    border: NODE_COLORS.defaultBorder,
    background: NODE_COLORS.defaultBackground,
  };
}

function layoutGraph({ nodes, edges }: { nodes: Node[]; edges: Edge[] }): {
  nodes: Node[];
  edges: Edge[];
} {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 60 });

  for (const node of nodes) {
    graph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const edge of edges) {
    graph.setEdge(edge.source, edge.target);
  }

  dagre.layout(graph);

  return {
    nodes: nodes.map((node) => {
      const position = graph.node(node.id);
      return {
        ...node,
        position: {
          x: position.x - NODE_WIDTH / 2,
          y: position.y - NODE_HEIGHT / 2,
        },
      };
    }),
    edges,
  };
}

function buildFlowElements(graph: ParsedAdGraphOutput): {
  nodes: Node[];
  edges: Edge[];
} {
  const cappedNodes = graph.nodes.slice(0, MAX_CHAT_GRAPH_NODES);
  const nodeIds = new Set(cappedNodes.map((node) => node.id));
  const nodes: Node[] = cappedNodes.map((node) => {
    const colors = resolveNodeColors(node);
    return {
      id: node.id,
      data: { label: `${node.kind}: ${node.label}` },
      position: { x: 0, y: 0 },
      style: {
        fontSize: 11,
        padding: '6px 10px',
        borderRadius: 6,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        width: NODE_WIDTH,
      },
    };
  });

  const edges: Edge[] = graph.edges
    .filter((edge) => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId))
    .map((edge, index) => {
      const strokeColor = edge.isAbusable
        ? NODE_COLORS.abusableEdge
        : NODE_COLORS.defaultEdge;
      return {
        id: `${edge.sourceId}-${edge.relationshipType}-${edge.targetId}-${index}`,
        source: edge.sourceId,
        target: edge.targetId,
        label: edge.relationshipType,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 16,
          height: 16,
          color: strokeColor,
        },
        style: {
          strokeWidth: edge.isAbusable ? 1.75 : 1.25,
          stroke: strokeColor,
        },
        labelStyle: { fontSize: 10 },
      };
    });

  return layoutGraph({ nodes, edges });
}

function getGraphStructureKey(graph: ParsedAdGraphOutput): string {
  const nodeKey = graph.nodes
    .map(
      (node) =>
        `${node.id}:${node.kind}:${node.label}:${node.isCrownJewel === true}:${node.isOwned === true}`,
    )
    .join('|');
  const edgeKey = graph.edges
    .map(
      (edge) =>
        `${edge.sourceId}>${edge.relationshipType}>${edge.targetId}:${edge.isAbusable === true}`,
    )
    .join('|');
  return `${nodeKey}::${edgeKey}::${graph.truncated === true}::${graph.totalCount ?? ''}`;
}

function AdGraphChatFlowMounted({ graph }: { graph: ParsedAdGraphOutput }) {
  const [{ nodes: initialNodes, edges: initialEdges }] = useState(() =>
    buildFlowElements(graph),
  );
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  if (initialNodes.length === 0) {
    return null;
  }

  return (
    <Box
      width="100%"
      height="280px"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      overflow="hidden"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
}

export function AdGraphChatFlow({ graph }: { graph: ParsedAdGraphOutput }) {
  return (
    <AdGraphChatFlowMounted key={getGraphStructureKey(graph)} graph={graph} />
  );
}
